/*
 * Session state: /config, /me, the current workspace, and the spec 12.3 purchase gate.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { setCsrfToken } from "../api/client";
import { endpoints } from "../api/endpoints";
import type { ConfigResponse, MeResponse, Role, WorkspaceMembership } from "../api/types";
import { isMiniApp } from "../lib/tg";

export function useConfig() {
  return useQuery({ queryKey: ["config"], queryFn: endpoints.config, staleTime: Infinity });
}

export function useMeQuery() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const me = await endpoints.me();
      setCsrfToken(me.csrf_token);
      return me;
    },
    retry: false,
    staleTime: 60_000,
  });
}

type Session = {
  me: MeResponse;
  config: ConfigResponse;
  ws: string;
  membership: WorkspaceMembership;
  setWs: (id: string) => void;
  /** Purchases (top-ups, plan changes, checkout) exist only in the browser — spec 12.3 / C1. */
  canPurchase: boolean;
  can: (action: "manage" | "own" | "view_billing") => boolean;
};
const SessionCtx = createContext<Session | null>(null);

const WS_KEY = "beacon:ws";
const NO_WORKSPACE: WorkspaceMembership = { id: "", name: "", role: "owner", client_ids: null, plan_sku: "free", onboarded: false, pending_approvals: 0 };

export function SessionProvider({ me, config, children }: { me: MeResponse; config: ConfigResponse; children: ReactNode }) {
  const qc = useQueryClient();
  const [ws, setWsState] = useState(() => {
    const saved = localStorage.getItem(WS_KEY);
    return me.workspaces.find((w) => w.id === saved)?.id ?? me.workspaces[0]?.id ?? "";
  });
  useEffect(() => {
    if (!me.workspaces.some((w) => w.id === ws) && me.workspaces[0]) setWsState(me.workspaces[0].id);
  }, [me.workspaces, ws]);
  const setWs = useCallback(
    (id: string) => {
      localStorage.setItem(WS_KEY, id);
      setWsState(id);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] === "w" });
    },
    [qc],
  );
  // Before the first workspace exists (onboarding), a blank owner membership keeps consumers simple.
  const membership = me.workspaces.find((w) => w.id === ws) ?? me.workspaces[0] ?? NO_WORKSPACE;
  const value = useMemo<Session>(() => {
    const role: Role = membership.role;
    return {
      me,
      config,
      ws,
      membership,
      setWs,
      canPurchase: !isMiniApp && me.session.surface === "web",
      can: (action) =>
        action === "own" ? role === "owner" : action === "manage" ? role === "owner" || role === "manager" : role !== "client_viewer",
    };
  }, [me, config, ws, membership, setWs]);
  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionCtx);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}
export const useWs = () => useSession().ws;

/** Query key helper: everything workspace-scoped starts with ["w", ws, …] so switching workspaces drops it. */
export const wk = (ws: string, ...rest: unknown[]) => ["w", ws, ...rest] as const;
